class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    this.addEventListener('click', (event) => {
      if (!this.classList.contains('active')) return;

      const clickedInsideDrawer = event.target.closest('.drawer__inner');
      const clickedCloseButton = event.target.closest('.drawer__close');

      if (clickedCloseButton || !clickedInsideDrawer) {
        event.preventDefault();
        this.close();
      }
    });

    this.setHeaderCartIconAccessibility();
    this.lastAddedVariantIds = [];

    // Serializa os cliques de cross-sell: cada clique só lê o carrinho
    // e adiciona depois que o clique anterior terminou de principio a fim.
    // Isso elimina a condição de corrida onde dois cliques quase
    // simultâneos liam o mesmo estado de carrinho desatualizado.
    this.crossSellQueue = Promise.resolve();

    // Cada chamada a refreshDrawerSections() recebe um número de sequência.
    // Se uma resposta de rede chegar fora de ordem (mais lenta que uma
    // chamada mais recente), ela é descartada em vez de sobrescrever o
    // drawer com um HTML desatualizado.
    this.drawerRefreshSequence = 0;

    this.handleCrossSellClick = this.handleCrossSellClick.bind(this);

    document.addEventListener(
      'click',
      this.handleCrossSellClick
    );
  }

  async handleCrossSellClick(event) {
    const button = event.target.closest('.gelada-cross-sell-btn');

    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.dataset.processing === 'true') {
      return;
    }

    const variantId = String(
      button.dataset.variantId || ''
    );

    const targetQuantity = Math.max(
      1,
      parseInt(button.dataset.quantity || '1', 10)
    );

    if (!variantId) {
      console.error(
        'Cross-sell Gelada: variantId não encontrado.'
      );

      return;
    }

    // Encadeia esta operação na fila em vez de disparar imediatamente.
    // Assim, se o usuário clicar em dois botões de cross-sell em sequência
    // rápida, a segunda operação só lê /cart.js depois que a primeira
    // (leitura + adição + refresh do drawer) terminou completamente,
    // eliminando a leitura de estado desatualizado do carrinho.
    this.crossSellQueue = this.crossSellQueue.then(() =>
      this.addCrossSellProduct(button, variantId, targetQuantity)
    );

    await this.crossSellQueue;
  }

  async addCrossSellProduct(
  button,
  variantId,
  targetQuantity
) {
    const originalText = button.textContent;

    button.dataset.processing = 'true';
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = '...';

    try {
      const cartResponse = await fetch(
        `${routes.cart_url}.js?_=${Date.now()}`,
        {
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!cartResponse.ok) {
        throw new Error(
          'Não foi possível consultar o carrinho.'
        );
      }

      const cart = await cartResponse.json();

      const currentQuantity = cart.items
        .filter(
          (item) =>
            String(item.variant_id) === String(variantId)
        )
        .reduce(
          (total, item) => total + item.quantity,
          0
        );

      const quantityToAdd = Math.max(
        0,
        targetQuantity - currentQuantity
      );

      if (quantityToAdd === 0) {
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.textContent = '✓';

        setTimeout(() => {
          this.resetCrossSellButton(
            button,
            originalText
          );
        }, 1200);

        return;
      }

      const addResponse = await fetch(
        `${routes.cart_add_url}.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantityToAdd
          })
        }
      );

      if (!addResponse.ok) {
        const errorData = await addResponse
          .json()
          .catch(() => ({}));

        throw new Error(
          errorData.description ||
          'Não foi possível adicionar o produto.'
        );
      }

      this.lastAddedVariantIds = [
        String(variantId)
      ];

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.textContent = '✓';

      await this.refreshDrawerSections();

      setTimeout(() => {
        this.resetCrossSellButton(
          button,
          originalText
        );
      }, 1200);
    } catch (error) {
      console.error(
        'Erro no cross-sell Gelada:',
        error
      );

      button.classList.remove('is-loading');
      button.classList.add('is-error');
      button.textContent = '!';

      setTimeout(() => {
        this.resetCrossSellButton(
          button,
          originalText
        );
      }, 1500);
    }
  }

  async refreshDrawerSections() {
    const requestSequence = ++this.drawerRefreshSequence;

    const response = await fetch(
      `/?sections=cart-drawer,cart-icon-bubble&_=${Date.now()}`
    );

    if (!response.ok) {
      throw new Error(
        'Não foi possível atualizar o carrinho.'
      );
    }

    const sections = await response.json();

    // Se, enquanto esta requisição estava em voo, uma chamada mais
    // recente a refreshDrawerSections() já foi disparada, esta resposta
    // está desatualizada. Aplicá-la agora reintroduziria produtos/
    // quantidades antigos por cima do estado mais novo do carrinho.
    if (requestSequence !== this.drawerRefreshSequence) {
      return;
    }

    if (sections['cart-drawer']) {
      const parser = new DOMParser();

      const parsedDocument = parser.parseFromString(
        sections['cart-drawer'],
        'text/html'
      );

      const newDrawer =
        parsedDocument.querySelector('cart-drawer');

      if (newDrawer) {
        this.innerHTML = newDrawer.innerHTML;
      }
    }

    if (sections['cart-icon-bubble']) {
      const parser = new DOMParser();

      const parsedBubble = parser.parseFromString(
        sections['cart-icon-bubble'],
        'text/html'
      );

      const newCartIcon =
        parsedBubble.querySelector(
          '#cart-icon-bubble'
        );

      const currentCartIcon =
        document.querySelector(
          '#cart-icon-bubble'
        );

      if (newCartIcon && currentCartIcon) {
        currentCartIcon.innerHTML =
          newCartIcon.innerHTML;
      }
    }

    this.classList.add('animate');
    this.classList.add('active');

    document.body.classList.add(
      'overflow-hidden'
    );

    this.highlightRecentlyAddedItems({});

    this.updateCartIconBubble();

    if (
      typeof window.atualizarBarraFreteGelada ===
      'function'
    ) {
      setTimeout(() => {
        window.atualizarBarraFreteGelada();
      }, 300);
    }
  }

  resetCrossSellButton(
  button,
  originalText
) {
    if (!button) return;

    button.classList.remove(
      'is-loading',
      'is-added',
      'is-error'
    );

    button.disabled = false;
    button.dataset.processing = 'false';
    button.textContent = originalText || '+';
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.classList.add('animate');

    requestAnimationFrame(() => {
      this.classList.add('active');
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.open();
    this.highlightRecentlyAddedItems(parsedState);

    setTimeout(() => {
      this.updateCartIconBubble();
    }, 250);

    const total = this.querySelector('.totals__total-value');

    if (total) {
      total.classList.remove('gelada-total-highlight');

      setTimeout(() => {
        total.classList.add('gelada-total-highlight');
      }, 50);
    }

    const smartShipping = this.querySelector('.gelada-smart-shipping');

    if (smartShipping && smartShipping.textContent.includes('Entrega grátis liberada')) {
      smartShipping.classList.remove('gelada-free-shipping-unlocked');

      setTimeout(() => {
        smartShipping.classList.add('gelada-free-shipping-unlocked');
      }, 80);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  highlightRecentlyAddedItems(parsedState) {
    const ids = [];

    if (parsedState && parsedState.id) {
      ids.push(String(parsedState.id));
    }

    if (this.lastAddedVariantIds && this.lastAddedVariantIds.length) {
      ids.push(...this.lastAddedVariantIds.map(String));
    }

    if (!ids.length) return;

    ids.forEach((variantId) => {
      const badge = this.querySelector(`[data-added-badge="${variantId}"]`);
      if (!badge) return;

      const item = badge.closest('.cart-item');
      if (item) item.classList.add('gelada-item-added-now');

      badge.classList.add('is-visible');

      setTimeout(() => {
        badge.classList.remove('is-visible');
        if (item) item.classList.remove('gelada-item-added-now');
      }, 2800);
    });

    this.lastAddedVariantIds = [];
  }

  updateCartIconBubble() {
    fetch(`${routes.cart_url}.js?_=${Date.now()}`)
      .then((response) => response.json())
      .then((cart) => {
        const cartLink = document.querySelector('#cart-icon-bubble');
        if (!cartLink) return;

        const count = cart.item_count || 0;
        let bubble = cartLink.querySelector('.cart-count-bubble');

        if (!bubble && count > 0) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          cartLink.appendChild(bubble);
        }

        if (bubble) {
          if (count > 0) {
            bubble.innerHTML = `
              <span aria-hidden="true">${count}</span>
              <span class="visually-hidden">${count} itens no carrinho</span>
            `;
            bubble.style.display = '';
          } else {
            bubble.remove();
          }
        }

        cartLink.setAttribute('aria-label', `Carrinho, ${count} itens`);

        cartLink.classList.remove('gelada-cart-bubble-pulse');

        setTimeout(() => {
          cartLink.classList.add('gelada-cart-bubble-pulse');
        }, 50);
      })
      .catch((error) => {
        console.error('Erro ao atualizar contador da sacola:', error);
      });
  }

}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);